import { createContext, useContext } from 'react';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoEditorResponse, MusicVideoSongMap } from 'fym-shared/src/music24/musicVideoApi';
import type { CutSaveState } from './doc/useCutAutosave';
import type { ChangeDoc, MusicVideoDocDispatch } from './doc/useMusicVideoDoc';
import type { FootageCatalog } from './footage/useFootageCatalog';
import type { PreviewTransport } from './preview/usePreviewTransport';
import type { ShowcaseUpsellAction } from './showcase/showcaseActions';

/**
 * `account`: the editor of a song of the account (saves, the director, the account routes).
 * `showcase`: the public demo `/app/music-video-demo` (spec 2026-10-10 Studio showcase §4): the
 * edits stay in memory, nothing calls an account route, and every make or save action opens the
 * sign-up card.
 */
export type MusicVideoEditorMode = 'account' | 'showcase';

/** The tools of the rail (design b1). */
export const MUSIC_VIDEO_TOOLS = ['footage', 'text', 'lyrics', 'look'] as const;
export type MusicVideoTool = (typeof MUSIC_VIDEO_TOOLS)[number];

/** The fixed texts of the Text lane. A custom text is `custom:<id>`. */
export type MusicVideoTextKey = 'title' | 'name' | 'endCard' | `custom:${string}`;

/** What the inspector shows (design b1, d4: nothing selected = the video settings). */
export type MusicVideoSelection =
  | { kind: 'clip'; id: string }
  | { kind: 'text'; key: MusicVideoTextKey }
  | { kind: 'lyric'; index: number }
  | { kind: 'effect'; id: string }
  | null;

/** The section box of the timeline (design c6): one section, or the whole video. */
export type RecutTarget = number | 'all';

export interface MusicVideoEditorContextValue {
  mode: MusicVideoEditorMode;
  /** 0 in showcase mode: there is no account, and no account route is called. */
  accountId: number;
  /** A fixed key in showcase mode (`SHOWCASE_ITEM_KEY`): there is no catalog item. */
  catalogItemUuid: string;
  cutUuid: string;
  data: MusicVideoEditorResponse;
  /** The song map of the master. The editor opens only with one (frame e6 otherwise). */
  songMap: MusicVideoSongMap;
  doc: MusicVideoDoc;
  change: ChangeDoc;
  dispatch: MusicVideoDocDispatch;
  canUndo: boolean;
  canRedo: boolean;
  /** The example artist, or a cut that the director writes now: nothing is saved. */
  readOnly: boolean;
  selection: MusicVideoSelection;
  select: (selection: MusicVideoSelection) => void;
  /** The section of the timeline zoom, the "For <section>" list and the recut box. */
  sectionIndex: number | null;
  setSectionIndex: (index: number | null) => void;
  /** Opens the recut box (design c6) on a section or on the whole video. */
  openRecut: (target: RecutTarget) => void;
  footage: FootageCatalog;
  transport: PreviewTransport;
  /** Saves the open cut at once and waits (before a recut, a make, a cut switch); answers the save state. */
  flushSave: () => Promise<CutSaveState>;
  /** Opens another cut of the song (the cut menu, the cuts of the video settings). */
  openCut: (cutUuid: string) => void;
  tool: MusicVideoTool;
  setTool: (tool: MusicVideoTool) => void;
  /** Showcase mode: opens the sign-up card of an action that the demo does not run. Account mode: nothing. */
  showUpsell: (action: ShowcaseUpsellAction) => void;
}

export const MusicVideoEditorContext = createContext<MusicVideoEditorContextValue | null>(null);

/** The open editor. Every part of the editor is inside its provider. */
export function useMusicVideoEditor(): MusicVideoEditorContextValue {
  const value = useContext(MusicVideoEditorContext);
  if (value === null) {
    throw new Error('useMusicVideoEditor needs the MusicVideoEditorContext provider');
  }
  return value;
}
